import { merge } from 'lodash';
import legacyLocaleTextEn from '@meitu/x-design-locales-legacy/chunks/xDesignWidgets/en.json';
import legacyLocaleTextEs from '@meitu/x-design-locales-legacy/chunks/xDesignWidgets/es.json';
import legacyLocaleTextId from '@meitu/x-design-locales-legacy/chunks/xDesignWidgets/id.json';
import legacyLocaleTextIt from '@meitu/x-design-locales-legacy/chunks/xDesignWidgets/it.json';
import legacyLocaleTextPt from '@meitu/x-design-locales-legacy/chunks/xDesignWidgets/pt.json';
import legacyLocaleTextCn from '@meitu/x-design-locales-legacy/chunks/xDesignWidgets/zh-Hans.json';
import type { XDesignWidgetsLocaleText } from '@meitu/x-design-locales-legacy/types';
import nextLocaleTextEn from '@meitu/x-design-locales-next/en.json';
import nextLocaleTextEs from '@meitu/x-design-locales-next/es.json';
import nextLocaleTextId from '@meitu/x-design-locales-next/id.json';
import nextLocaleTextIt from '@meitu/x-design-locales-next/it.json';
import nextLocaleTextPt from '@meitu/x-design-locales-next/pt.json';
import type { NextLocaleText } from '@meitu/x-design-locales-next/types';
import nextLocaleTextCn from '@meitu/x-design-locales-next/zh-Hans.json';
import {
    createLocaleTextWithDevI18nKey,
    getLocale,
    setLocale,
    useLocaleText,
    type LocaleTextPrinter,
} from '@meitu/xdesign-i18n';

export type LocaleText = NextLocaleText & XDesignWidgetsLocaleText;
const LEGACY_CHUNK_KEY = 'xDesignWidgets';

/**
 * 独立发包场景：内置 zh-Hans，保证无外部注入时可用。
 * 业务应用可只走 CDN 注入，不必在业务代码里再 import 语言包 JSON。
 */
export const localeTextMap: Record<string, LocaleText> = {
    'zh-Hans': merge({}, nextLocaleTextCn, legacyLocaleTextCn),
    // TODO: 以下语言在全量上线后，可移除
    en: merge({}, nextLocaleTextEn, legacyLocaleTextEn),
    es: merge({}, nextLocaleTextEn, nextLocaleTextEs, legacyLocaleTextEn, legacyLocaleTextEs),
    pt: merge({}, nextLocaleTextEn, nextLocaleTextPt, legacyLocaleTextEn, legacyLocaleTextPt),
    id: merge({}, nextLocaleTextEn, nextLocaleTextId, legacyLocaleTextEn, legacyLocaleTextId),
    it: merge({}, nextLocaleTextEn, nextLocaleTextIt, legacyLocaleTextEn, legacyLocaleTextIt),
};

export type LocaleCode = keyof typeof localeTextMap;

export const SUPPORT_LOCALES = Object.keys(localeTextMap);

function mergeLocaleText(
    locale: LocaleCode,
    fallback: LocaleText,
    nextLocaleMap: Record<string, unknown>,
    devNextOverrides: Record<string, unknown>,
    legacyLocaleMap: Record<string, unknown>,
) {
    localeTextMap[locale] = merge({}, fallback, nextLocaleMap, devNextOverrides, legacyLocaleMap) as LocaleText;
}

// 浏览器环境外部注入多语言
if (typeof window !== 'undefined' && window.__LOCALE__) {
    const locale = window.__LOCALE__;
    const fallback = localeTextMap[locale] ?? localeTextMap['zh-Hans'];
    const nextLocaleMap = window.__LOCALE_TEXT_NEXT_MAP__ ?? {};
    const devNextOverrides = window.__DEV_NEXT_LOCALE_OVERRIDES__ ?? {};
    const legacyLocaleMap = window.__LOCALE_TEXT_LEGACY_MAP__?.[LEGACY_CHUNK_KEY] ?? {};

    mergeLocaleText(locale, fallback, nextLocaleMap, devNextOverrides, legacyLocaleMap);
} else if (typeof globalThis !== 'undefined') {
    // node.js 环境：__FULL_LOCALE_TEXT_MAP__ 已是 next + legacy 的全量结果
    // 开发态仅会注入一种语言，__DEV_NEXT_LOCALE_OVERRIDES__ 可直接覆盖到所有语言
    const localeGlobal = globalThis as {
        __FULL_LOCALE_TEXT_MAP__?: Record<string, LocaleText>;
        __DEV_NEXT_LOCALE_OVERRIDES__?: Record<string, unknown>;
    };
    const fullLocaleText = localeGlobal.__FULL_LOCALE_TEXT_MAP__;

    if (fullLocaleText) {
        const devNextOverrides = localeGlobal.__DEV_NEXT_LOCALE_OVERRIDES__ ?? {};
        for (const loc of Object.keys(fullLocaleText)) {
            const locale = loc as LocaleCode;
            const fallback = localeTextMap[locale] ?? localeTextMap['zh-Hans'];

            mergeLocaleText(
                locale,
                fallback,
                fullLocaleText[loc]! as unknown as Record<string, unknown>,
                devNextOverrides,
                {},
            );
        }
    }
}

type LocaleStore = {
    locale: LocaleCode;
    $t: LocaleTextPrinter<LocaleText>;
};

const localStoreCache: Partial<Record<LocaleCode, LocaleStore>> = {};

export const initLocaleStore = (locale: LocaleCode) => {
    setLocale(locale);
};

export const getLocaleStore = (_locale?: LocaleCode) => {
    const locale: LocaleCode = _locale || getLocale() || 'zh-Hans';
    const localeText = localeTextMap[locale] as unknown as Record<string, unknown>;

    if (!localStoreCache[locale]) {
        const monitoredLocaleText =
            globalThis.__I18N_MISSING_KEY_MONITOR__?.(localeText, {
                locale,
                namespace: LEGACY_CHUNK_KEY,
            }) ?? localeText;
        localStoreCache[locale] = {
            locale,
            $t: createLocaleTextWithDevI18nKey(monitoredLocaleText, LEGACY_CHUNK_KEY),
        } as unknown as LocaleStore;
    }

    return localStoreCache[locale]!;
};

export const useLocaleStore = () => {
    const locale = useLocaleText<LocaleCode>();

    return getLocaleStore(locale);
};

/**
 * useLocaleStore 的别名，不再推荐使用
 */
export const useLocale = useLocaleStore;
